import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Home, Clock, DollarSign, User, LogOut, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BrandLogo from '@/components/BrandLogo';

const navItems = [
  { to: '/driver', icon: Home, label: 'Home', end: true },
  { to: '/driver/history', icon: Clock, label: 'History' },
  { to: '/driver/payments', icon: DollarSign, label: 'Payments' },
  { to: '/driver/profile', icon: User, label: 'Profile' },
];

export default function DriverLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col md:flex-row" data-testid="driver-layout">
      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex ${collapsed ? 'w-16' : 'w-56'} bg-sidebar border-r border-sidebar-border flex-col transition-all duration-200 shrink-0`}>
        <div className="p-4 flex items-center justify-between">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <BrandLogo size="sm" />
              <span className="text-[10px] text-muted-foreground">Driver</span>
            </div>
          )}
          <Button variant="ghost" size="icon" onClick={() => setCollapsed(!collapsed)} className="text-muted-foreground hover:text-foreground h-8 w-8">
            {collapsed ? <Menu className="w-4 h-4" /> : <X className="w-4 h-4" />}
          </Button>
        </div>

        <nav className="flex-1 py-4 space-y-1 px-2">
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? 'bg-sidebar-active text-sidebar-active-foreground border border-brand-accent/20' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-transparent'
                }`
              }>
              <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-sidebar-border">
          <Button variant="ghost" onClick={handleLogout}
            className="w-full flex items-center gap-2 text-muted-foreground hover:text-red-500 justify-start text-sm">
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            {!collapsed && <span>Sign Out</span>}
          </Button>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-h-screen md:min-h-0 md:overflow-hidden">
        {/* Mobile header */}
        <header className="md:hidden bg-background border-b border-border px-4 py-3 flex items-center justify-between shrink-0">
          <BrandLogo size="default" />
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{user?.name}</span>
            <button onClick={handleLogout} className="text-muted-foreground hover:text-red-500" data-testid="driver-logout-btn">
              <LogOut className="w-5 h-5" strokeWidth={2} />
            </button>
          </div>
        </header>

        {/* Desktop header */}
        <header className="hidden md:flex bg-sidebar border-b border-sidebar-border px-6 py-3 items-center justify-end gap-4 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-brand-accent/20 flex items-center justify-center text-sm font-bold text-brand-accent">
              {user?.name?.charAt(0)?.toUpperCase() || 'D'}
            </div>
            <span className="text-sm text-muted-foreground">{user?.name}</span>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto px-4 py-4 pb-20 md:pb-6 max-w-lg md:max-w-none mx-auto md:mx-0 w-full">
          <div className="md:p-2">
            <Outlet />
          </div>
        </main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-background border-t border-border flex items-center justify-around py-2 px-4 z-50"
          data-testid="driver-bottom-nav">
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-1 px-4 min-h-[48px] justify-center transition-colors ${
                  isActive ? 'text-brand-accent' : 'text-muted-foreground'
                }`
              }>
              <item.icon className="w-6 h-6" strokeWidth={2} />
              <span className="text-[10px] font-bold">{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
